import {useMemo} from 'react'
import {CustomerModel} from '../../../models/CustomerModel'
import {ImageInputValue} from '../../../components/inputs/FileInput/ImageInputValue'
import {useAppConfig} from '../../app-config/hooks/useAppConfig'
import {DetailHeaderContent} from '../../../components/DetailHeader/DetailHeaderContent'
import {BookingModel} from '../../../models/ems/BookingModel'
import {useBookingDataUtils} from '../hooks/useBookingDataUtils'
import {DataButton} from '../../../components/inputs/DataButton'
import {bookingIsCancellable} from './tables/BookingTable/BookingTableActions'
import {MetronicIcon} from '../../../components/inputs/MetronicIcon'
import {BookingStatusColumn} from './tables/BookingTable/BookingStatusColumn'
import {DateFormatter} from '../../../components/utils/formatter/DateFormatter'

export interface CustomerDetailHeaderProps {
  customer?: CustomerModel | null
  booking?: BookingModel | null
  onRefresh?: () => void
  onRefreshCallback?: () => void
}

export const CustomerDetailHeader = ({
  customer,
  onRefresh,
  booking,
  onRefreshCallback,
}: CustomerDetailHeaderProps) => {
  const {staticUrls} = useAppConfig()
  const {cancelSingle, isBookingLoading} = useBookingDataUtils({onRefresh, onRefreshCallback})
  const avatar = useMemo(() => {
    if (customer?.photo) {
      const imageValue = new ImageInputValue(staticUrls.public, customer.photo)
      return imageValue.url
    }
  }, [customer?.photo, staticUrls.public])

  return (
    <>
      <DetailHeaderContent
        left={
          <div className='d-flex flex-row'>
            {avatar && (
              <div className='d-flex drag-drop-file-input-drop-area-thumbnail'>
                <img src={avatar} alt='' className=' h-100' />
              </div>
            )}

            <div className='d-flex flex-column px-2'>
              <div className='d-flex align-items-center'>
                <p className='text-gray-800 fs-2 fw-bolder mb-1'>{customer?.name}</p>
              </div>
              <div className='d-flex flex-wrap'>
                <div className='d-flex flex-column me-3 mt-3'>
                  {customer?.mobile && customer.mobile.length > 0 && (
                    <div className='d-flex flex-wrap'>
                      <span className='d-flex align-items-center'>
                        <MetronicIcon iconType='Devices' iconName='Phone' size='svg-icon-4' />
                        {customer.mobile}
                      </span>
                    </div>
                  )}
                  {customer?.email && customer.email.length > 0 && (
                    <div className='d-flex flex-wrap'>
                      <span className='d-flex align-items-center text-muted'>
                        <MetronicIcon iconType='Communication' iconName='Mail' size='svg-icon-4' />
                        {customer.email}
                      </span>
                    </div>
                  )}
                </div>
                <div className='d-flex flex-column mt-3'>
                  {booking && (
                    <>
                      <div className='d-flex flex-wrap'>
                        <span className='me-1 fw-bolder'>Booking:</span>
                        <span className='me-1'>{booking.code}</span>
                      </div>
                      <div className='d-flex flex-wrap'>
                        <span className='me-1 fw-bolder'>Status:</span>
                        <BookingStatusColumn data={booking} />
                      </div>

                      <div className='d-flex flex-wrap'>
                        <span className='me-1 fw-bolder'>Date:</span>
                        <DateFormatter className='text-uppercase'>
                          {booking.updatedAt}
                        </DateFormatter>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        }
        right={
          <div className='d-flex my-4 flex-wrap'>
            {booking && (
              <>
                {bookingIsCancellable(booking) && (
                  <DataButton
                    disabled={isBookingLoading(booking)}
                    onClick={cancelSingle}
                    data={booking}
                    className='min-w-125px'
                    variant='text'
                    color='danger'
                  >
                    <MetronicIcon iconType='Code' iconName='Stop' />
                    Cancel
                  </DataButton>
                )}
              </>
            )}
          </div>
        }
      />
    </>
  )
}
